/**
 * useDeepLink — URL-driven map navigation from notifications
 * 
 * When a user clicks a notification that points to /dashboard?project=123,
 * this hook reads the URL params and orchestrates:
 * 1. Zoom/pan the cosmic map to center on the target project node
 * 2. Highlight the project's lineage connections
 * 3. Open the project detail overlay
 * 
 * Also supports:
 * - ?highlight=lineage — lights up the full fork chain
 * - ?branch=456 — activates a curated branch
 * - ?challenge=789 — opens a challenge overlay
 * - ?channel=101 — opens the messaging drawer to a specific channel
 * - ?create — opens the create project overlay
 * - ?create-challenge — opens the create challenge overlay
 *
 * Place at: src/hooks/useDeepLink.js
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const useDeepLink = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [consumed, setConsumed] = useState(false);

  // Read params (only if not yet consumed)
  const deepLinkProjectId = !consumed
    ? parseInt(searchParams.get('project')) || null
    : null;

  // focus= selects + highlights on map WITHOUT opening the detail overlay
  const deepLinkFocusId = !consumed
    ? parseInt(searchParams.get('focus')) || null
    : null;

  const deepLinkChallengeId = !consumed
    ? parseInt(searchParams.get('challenge')) || null
    : null;

  const deepLinkChannelId = !consumed
    ? parseInt(searchParams.get('channel')) || null
    : null;

  const deepLinkBranchId = !consumed
    ? parseInt(searchParams.get('branch')) || null
    : null;

  const shouldHighlightLineage = !consumed
    ? searchParams.get('highlight') === 'lineage'
    : false;

  // New: create and create-challenge params
  // These are flag params (present = true), not value params
  const shouldOpenCreate = !consumed
    ? searchParams.has('create')
    : false;

  const shouldOpenCreateChallenge = !consumed
    ? searchParams.has('create-challenge')
    : false;

  // multiselect=1,2,3&action=fork|collect — from FeedSelectionBar
  const deepLinkMultiSelect = !consumed
    ? (searchParams.get('multiselect') || '').split(',').map(Number).filter(Boolean)
    : [];
  const deepLinkMultiAction = !consumed
    ? searchParams.get('action') || null
    : null;

  // Clear deep link params from URL after consumption
  const clearDeepLink = useCallback(() => {
    setConsumed(true);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('project');
    newParams.delete('focus');
    newParams.delete('challenge');
    newParams.delete('channel');
    newParams.delete('branch');
    newParams.delete('highlight');
    newParams.delete('create');
    newParams.delete('create-challenge');
    newParams.delete('multiselect');
    newParams.delete('action');

    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Reset consumed state when URL changes (new notification click)
  useEffect(() => {
    const projectParam = searchParams.get('project');
    const focusParam = searchParams.get('focus');
    const challengeParam = searchParams.get('challenge');
    const channelParam = searchParams.get('channel');
    const hasCreate = searchParams.has('create');
    const hasCreateChallenge = searchParams.has('create-challenge');
    const hasMultiSelect = searchParams.has('multiselect');

    if (projectParam || focusParam || challengeParam || channelParam || hasCreate || hasCreateChallenge || hasMultiSelect) {
      setConsumed(false);
    }
  }, [searchParams]);

  return {
    deepLinkProjectId,
    deepLinkFocusId,
    deepLinkChallengeId,
    deepLinkChannelId,
    deepLinkBranchId,
    shouldHighlightLineage,
    shouldOpenCreate,
    shouldOpenCreateChallenge,
    deepLinkMultiSelect,
    deepLinkMultiAction,
    clearDeepLink,
    hasDeepLink: !consumed && (
      !!deepLinkProjectId || !!deepLinkFocusId || !!deepLinkChallengeId ||
      !!deepLinkChannelId || !!deepLinkBranchId || shouldOpenCreate || shouldOpenCreateChallenge ||
      deepLinkMultiSelect.length > 0
    ),
  };
};

export default useDeepLink;